/**
 * Geometry for fitting a scan onto a print size.
 *
 * Three surfaces have to agree on exactly how a frame lands on the paper: the
 * per-line preview in the basket, the crop dialog the customer drags around,
 * and the render `conversion_worker` performs against the full-resolution
 * master. Keeping the maths here (pure, no React) is what lets the preview be
 * a promise rather than an approximation.
 *
 * Two rules underpin everything below:
 *
 * 1. **Paper follows the image's orientation.** A 6x8 sheet is 8 units on its
 *    long edge whichever way round it is printed, so a portrait scan is fitted
 *    to portrait paper, not rotated or letterboxed onto a landscape sheet.
 * 2. **No crop means nothing is lost.** `crop === null` is the default and
 *    means "letterbox the whole frame", with white filling whatever the aspect
 *    mismatch leaves over. A crop only ever exists because the customer made
 *    one.
 */

import { centredCropForAspect, clampCrop, type CropSpec } from './editRecipe';

/**
 * Long-edge : short-edge ratio for each print size. `4x6` is a 4-inch by
 * 6-inch sheet, so its long edge is 1.5x its short edge.
 */
export const PRINT_SIZE_RATIOS: Record<string, number> = {
  '4x6': 6 / 4,
  '6x8': 8 / 6,
};

/** Zoom of 1 means "fill the paper" — the largest crop of the paper's aspect. */
export const MIN_ZOOM = 1;
/** Beyond ~3x a 4x6 print starts showing scan grain rather than detail. */
export const MAX_ZOOM = 3;

/**
 * The paper's aspect (width / height) for an image of the given aspect,
 * oriented to match the image. Falls back to the image's own aspect for an
 * unknown size, which letterboxes to nothing rather than guessing a shape.
 */
export function paperAspect(size: string, imageAspect: number): number {
  const ratio = PRINT_SIZE_RATIOS[size];
  if (!ratio || !Number.isFinite(imageAspect) || imageAspect <= 0) return imageAspect;
  // A square image has no orientation to follow; landscape is the tie-break.
  return imageAspect >= 1 ? ratio : 1 / ratio;
}

/**
 * Letterbox geometry: the fraction of the paper the image occupies when the
 * whole frame is fitted inside it. One of the two is always 1 — the other is
 * how much of that axis is image rather than white.
 */
export interface LetterboxFit {
  widthFraction: number;
  heightFraction: number;
}

export function letterboxFit(imageAspect: number, targetAspect: number): LetterboxFit {
  if (!Number.isFinite(imageAspect) || imageAspect <= 0) {
    return { widthFraction: 1, heightFraction: 1 };
  }
  return imageAspect >= targetAspect
    ? { widthFraction: 1, heightFraction: targetAspect / imageAspect }
    : { widthFraction: imageAspect / targetAspect, heightFraction: 1 };
}

/** True when fitting the whole frame leaves visible white on the paper. */
export function hasLetterboxing(imageAspect: number, targetAspect: number): boolean {
  const fit = letterboxFit(imageAspect, targetAspect);
  // Sub-half-percent slivers are rounding, not something worth telling anyone.
  return fit.widthFraction < 0.995 || fit.heightFraction < 0.995;
}

/**
 * Build the crop for a given zoom and centre.
 *
 * Zoom 1 is the largest crop of the paper's aspect that fits the image (the
 * "fill the sheet" framing); higher zooms take a proportionally smaller slice.
 * The centre is clamped so the crop can never run off the edge of the image,
 * which is what stops a drag from exposing blank space.
 */
export function cropForZoom(
  targetAspect: number,
  imageAspect: number,
  zoom: number,
  centreX: number,
  centreY: number,
): CropSpec {
  const base = centredCropForAspect(targetAspect, imageAspect);
  const scale = Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM);
  const width = base.width / scale;
  const height = base.height / scale;
  return clampCrop({
    x: centreX - width / 2,
    y: centreY - height / 2,
    width,
    height,
  });
}

/** The zoom a crop represents, relative to the fill-the-paper framing. */
export function zoomOfCrop(crop: CropSpec, targetAspect: number, imageAspect: number): number {
  const base = centredCropForAspect(targetAspect, imageAspect);
  if (crop.width <= 0) return MIN_ZOOM;
  const zoom = base.width / crop.width;
  return Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM);
}

/** The centre point of a crop, in normalised image coordinates. */
export function cropCentre(crop: CropSpec): { x: number; y: number } {
  return { x: crop.x + crop.width / 2, y: crop.y + crop.height / 2 };
}

/**
 * CSS placement of the image inside a paper-shaped box, as percentages.
 *
 * Used by both the basket preview and the crop dialog so the small preview and
 * the thing the customer drags cannot drift apart. The box is the sheet; the
 * image is absolutely positioned within it and anything outside is off the
 * paper.
 *
 * Filling: the image is scaled so the crop region exactly covers the box, then
 * shifted so the crop's origin lands at the box's origin. Letterboxing: the
 * whole frame is centred at whatever fraction of the box it occupies, and the
 * remainder shows the box's white.
 */
export interface FramingStyle {
  width: string;
  height: string;
  left: string;
  top: string;
}

export function framingStyle(
  crop: CropSpec | null,
  imageAspect: number,
  targetAspect: number,
): FramingStyle {
  if (crop) {
    return {
      width: `${(1 / crop.width) * 100}%`,
      height: `${(1 / crop.height) * 100}%`,
      left: `${(-crop.x / crop.width) * 100}%`,
      top: `${(-crop.y / crop.height) * 100}%`,
    };
  }
  const fit = letterboxFit(imageAspect, targetAspect);
  return {
    width: `${fit.widthFraction * 100}%`,
    height: `${fit.heightFraction * 100}%`,
    left: `${((1 - fit.widthFraction) / 2) * 100}%`,
    top: `${((1 - fit.heightFraction) / 2) * 100}%`,
  };
}

/**
 * Carry a crop across a print-size change.
 *
 * The customer framed a shot deliberately; a size change should move that
 * framing to the new aspect rather than discard it. Their centre is kept and
 * their zoom preserved as far as the new aspect allows, so the subject stays
 * put and only the edges of the rectangle move.
 */
export function refitCrop(
  crop: CropSpec,
  previousTargetAspect: number,
  nextTargetAspect: number,
  imageAspect: number,
): CropSpec {
  const zoom = zoomOfCrop(crop, previousTargetAspect, imageAspect);
  const centre = cropCentre(crop);
  return cropForZoom(nextTargetAspect, imageAspect, zoom, centre.x, centre.y);
}
